/**
 * Iter 63 — Universal confirmation dialog for destructive actions.
 *
 * Use:
 *   const [confirm, ConfirmHost] = useConfirm();
 *   ...
 *   if (await confirm({ title: 'Disable user?', body: '...', confirmLabel: 'Disable', danger: true })) { ... }
 *   ...
 *   return <>{ConfirmHost}<YourPage /></>;
 *
 * Or use <ConfirmDialog open onConfirm onCancel /> directly.
 */
import React, { useCallback, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';

export function ConfirmDialog({
  open,
  title = 'Are you sure?',
  body = 'This action cannot be undone.',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  requirePhrase = null,           // when set, user must type this exact phrase
  onConfirm,
  onCancel,
}) {
  const [typed, setTyped] = useState('');
  if (!open) return null;
  const phraseOk = !requirePhrase || typed.trim() === requirePhrase;

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="confirm-dialog-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden"
        data-testid="confirm-dialog"
        role="dialog"
        aria-modal="true"
      >
        <header className={`px-5 py-3 flex items-center justify-between ${danger ? 'bg-rose-50 border-b border-rose-200' : 'bg-slate-50 border-b border-slate-200'}`}>
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className={danger ? 'text-rose-600' : 'text-slate-600'} />
            <h2 className="font-display font-bold text-sm uppercase tracking-wide">{title}</h2>
          </div>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-700" data-testid="confirm-dialog-close" aria-label="Close">
            <X size={16} />
          </button>
        </header>
        <div className="px-5 py-4 text-sm text-slate-700 whitespace-pre-line" data-testid="confirm-dialog-body">{body}</div>
        {requirePhrase && (
          <div className="px-5 pb-3">
            <label className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500 block mb-1">
              Type <code className="bg-slate-100 px-1.5 py-0.5 rounded text-[11px] font-mono">{requirePhrase}</code> to confirm
            </label>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={requirePhrase}
              className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-rose-500"
              data-testid="confirm-dialog-phrase-input"
              autoFocus
            />
          </div>
        )}
        <footer className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-xs font-display font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-200 rounded-sm"
            data-testid="confirm-dialog-cancel"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={!phraseOk}
            className={`px-4 py-2 text-xs font-display font-bold uppercase tracking-wider text-white rounded-sm disabled:opacity-40 disabled:cursor-not-allowed ${
              danger ? 'bg-rose-600 hover:bg-rose-700' : 'bg-[#3b4872] hover:bg-[#2d3856]'
            }`}
            data-testid="confirm-dialog-confirm"
          >
            {confirmLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}

/**
 * Promise-based confirm hook.
 *
 * Returns [confirm, host]:
 *   confirm({...opts}) → Promise<boolean>
 *   host : JSX to mount once at the top of your component tree.
 */
export function useConfirm() {
  const [state, setState] = useState({ open: false });
  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      setState({ open: true, ...opts, _resolve: resolve });
    });
  }, []);
  const close = (result) => {
    state._resolve?.(result);
    setState({ open: false });
  };
  const host = (
    <ConfirmDialog
      open={state.open}
      title={state.title}
      body={state.body}
      confirmLabel={state.confirmLabel}
      cancelLabel={state.cancelLabel}
      danger={state.danger}
      requirePhrase={state.requirePhrase}
      onConfirm={() => close(true)}
      onCancel={() => close(false)}
    />
  );
  return [confirm, host];
}
